import { useRef, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

// ----------------------------------------------------------------------

interface Pointer {
  x: number;
  y: number;
  isDown: boolean;
  radius: number;
}

// Declare anime.js v4 types
interface AnimeUtils {
  random: (min: number, max: number, decimals?: number) => number;
  set: (targets: Element | Element[], props: Record<string, unknown>) => void;
}

interface AnimeInstance {
  animate: (
    targets: Element | Element[],
    props: Record<string, unknown>,
    options?: Record<string, unknown>
  ) => unknown;
  createTimer: (params: Record<string, unknown>) => { restart: () => void };
  utils: AnimeUtils;
}

declare global {
  interface Window {
    anime?: AnimeInstance;
  }
}

export function FireflyAnimation() {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const circleRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef<Pointer>({ x: 0, y: 0, isDown: false, radius: 0 });
  const viewportRef = useRef({ w: 0, h: 0 });
  const radiusTimeoutRef = useRef<{ restart: () => void } | null>(null);
  const baseRadiusRef = useRef(0);
  const activeRadiusRef = useRef(0);
  const particlesRef = useRef<HTMLDivElement[]>([]);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Get theme colors
  const primaryMain = theme.palette.primary.main;
  const primaryLight = theme.palette.primary.light;
  const secondaryMain = theme.palette.secondary.main;

  const loadAnimeJS = useCallback(
    (): Promise<void> =>
      new Promise((resolve, reject) => {
        if (window.anime) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/animejs@4.0.0/lib/anime.iife.min.js';
        script.async = true;
        script.onload = () => {
          // Small delay to ensure global is available
          setTimeout(() => {
            if (window.anime) {
              resolve();
            } else {
              reject(new Error('anime.js loaded but global not available'));
            }
          }, 50);
        };
        script.onerror = () => reject(new Error('Failed to load anime.js'));
        document.head.appendChild(script);
      }),
    []
  );

  const animateParticle = useCallback(($el: HTMLDivElement) => {
    const animeLib = window.anime;
    if (!animeLib) return null;

    return animeLib.createTimer({
      frameRate: 4,
      onUpdate: () => {
        const currentAnime = window.anime;
        if (!currentAnime) return;

        const angle = Math.random() * Math.PI * 2;
        const radius = pointerRef.current.isDown
          ? activeRadiusRef.current
          : pointerRef.current.radius;

        currentAnime.animate(
          $el,
          {
            translateX: Math.cos(angle) * radius + pointerRef.current.x,
            translateY: Math.sin(angle) * radius + pointerRef.current.y,
            scale: 0.5 + currentAnime.utils.random(0.1, 1),
          },
          {
            duration: currentAnime.utils.random(1000, 1500),
            ease: 'inOutQuad',
          }
        );
      },
    });
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const circle = circleRef.current;
    if (!container || !circle) return undefined;

    let mounted = true;
    const timers: Array<{ restart: () => void }> = [];

    const initAnimation = async () => {
      try {
        await loadAnimeJS();
      } catch (error) {
        console.error('Failed to load anime.js:', error);
        return;
      }

      if (!mounted || !window.anime) return;

      const animeLib = window.anime;

      // Calculate dimensions
      const rect = container.getBoundingClientRect();
      viewportRef.current = { w: rect.width * 0.5, h: rect.height * 0.5 };
      baseRadiusRef.current = Math.min(rect.width, rect.height) * 0.15;
      activeRadiusRef.current = Math.min(rect.width, rect.height) * 0.25;
      pointerRef.current.radius = baseRadiusRef.current;

      // Create radius timeout timer
      radiusTimeoutRef.current = animeLib.createTimer({
        duration: 150,
        autoplay: false,
        onComplete: () => {
          pointerRef.current.radius = baseRadiusRef.current;
        },
      });

      // Create particles
      const rows = 12;
      const colors = [primaryMain, primaryLight, secondaryMain];
      const particles: HTMLDivElement[] = [];

      for (let i = 0; i < rows * rows; i += 1) {
        const particle = document.createElement('div');
        particle.className = 'firefly-particle';
        const color = colors[Math.floor(animeLib.utils.random(0, colors.length - 0.01))];
        particle.style.color = color;
        particle.style.background = color;
        particle.style.boxShadow = `0px 0px 20px 4px ${color}, 0px 0px 40px 8px ${color}`;
        container.appendChild(particle);
        particles.push(particle);

        const timer = animateParticle(particle);
        if (timer) timers.push(timer);
      }

      particlesRef.current = particles;
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!container || !circle) return;

      const rect = container.getBoundingClientRect();
      pointerRef.current.x = e.clientX - rect.left - viewportRef.current.w;
      pointerRef.current.y = e.clientY - rect.top - viewportRef.current.h;
      pointerRef.current.radius = pointerRef.current.isDown
        ? activeRadiusRef.current
        : baseRadiusRef.current * 1.25;

      radiusTimeoutRef.current?.restart();

      if (window.anime) {
        window.anime.utils.set(circle, {
          translateX: pointerRef.current.x,
          translateY: pointerRef.current.y,
        });
      }
    };

    const handleMouseDown = () => {
      pointerRef.current.isDown = true;
      if (window.anime && circle) {
        window.anime.animate(
          circle,
          {
            scale: 0.5,
            opacity: 1,
            filter: 'saturate(1.25)',
          },
          { duration: 300 }
        );
      }
    };

    const handleMouseUp = () => {
      pointerRef.current.isDown = false;
      if (window.anime && circle) {
        window.anime.animate(
          circle,
          {
            scale: 1,
            opacity: 0.3,
            filter: 'saturate(1)',
          },
          { duration: 300 }
        );
      }
    };

    initAnimation();

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseUp);

    cleanupRef.current = () => {
      mounted = false;
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseUp);

      // Remove particles
      particlesRef.current.forEach((particle) => {
        if (particle.parentNode) {
          particle.parentNode.removeChild(particle);
        }
      });
      particlesRef.current = [];
    };

    return () => {
      cleanupRef.current?.();
    };
  }, [loadAnimeJS, animateParticle, primaryMain, primaryLight, secondaryMain]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        cursor: 'grab',
        userSelect: 'none',
        '&:active': {
          cursor: 'grabbing',
        },
        // Particle styles
        '& .firefly-particle': {
          opacity: 0.8,
          position: 'absolute',
          zIndex: 2,
          top: '50%',
          left: '50%',
          width: '12px',
          height: '12px',
          margin: '-6px 0 0 -6px',
          borderRadius: '50%',
          willChange: 'transform',
          mixBlendMode: 'plus-lighter',
        },
      }}
    >
      {/* Central glow circle */}
      <Box
        ref={circleRef}
        sx={{
          opacity: 0.4,
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '120px',
          height: '120px',
          margin: '-60px 0 0 -60px',
          borderRadius: '50%',
          willChange: 'transform, opacity',
          color: primaryMain,
          '&::before, &::after': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '50%',
          },
          '&::before': {
            zIndex: 1,
            opacity: 1,
            boxShadow: `inset 0px 0px 40px 0px ${primaryMain}, 0px 0px 60px 10px ${primaryMain}, 0px 0px 30px 5px ${primaryMain}`,
          },
          '&::after': {
            zIndex: 3,
            opacity: 0.75,
            boxShadow: `inset 0 0 50px 0 ${primaryMain}`,
            backgroundImage: `radial-gradient(circle at 50% 50%, ${primaryMain} 0%, transparent 100%)`,
          },
        }}
      />
    </Box>
  );
}
